/**
 * Super-admin · Atlas analytics.
 *
 * Per-model usage + cost over a date range: tasks done, API calls, input/output
 * tokens, and cost for DeepSeek vs Kimi — with a daily cost breakdown so a
 * provider mix (half a day DeepSeek, half Kimi) is visible at a glance.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";

import { api } from "@/lib/api";
import type { AtlasAnalytics } from "@/lib/types";

const MODEL_COLOR: Record<string, string> = {
  "deepseek-v4-pro": "#7a3dff",
  "kimi-k2.7-code": "#146ef5",
  "kimi-k3": "#00a2c7",
};
const colorFor = (m: string) => MODEL_COLOR[m] ?? "var(--mute)";

function isoDaysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
const fmtInt = (n: number) => n.toLocaleString();
const fmtUsd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;
// Share of input tokens served from the provider's prefix cache (billed cheaper).
const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-07-28" → "Jul 28" (string-based, so no timezone drift).
function fmtDate(iso: string) {
  const [, m, d] = iso.split("-");
  return `${MONTHS[Number(m) - 1]} ${Number(d)}`;
}

const PRESETS: { label: string; days: number }[] = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

export function AtlasAnalyticsPage() {
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(isoDaysAgo(0));

  const { data, isLoading } = useQuery({
    queryKey: ["atlas-analytics", from, to],
    queryFn: async () =>
      (
        await api.get<AtlasAnalytics>(
          `/superadmin/atlas-analytics?from_date=${from}&to_date=${to}`,
        )
      ).data,
  });

  // Group the series by day for the stacked cost bars.
  const byDay = useMemo(() => {
    const m = new Map<string, { model: string; cost_usd: number }[]>();
    for (const r of data?.series ?? []) {
      const arr = m.get(r.date) ?? [];
      arr.push({ model: r.model, cost_usd: r.cost_usd });
      m.set(r.date, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);
  const maxDayCost = Math.max(
    0.0001,
    ...byDay.map(([, rows]) => rows.reduce((a, r) => a + r.cost_usd, 0)),
  );

  const t = data?.totals;

  return (
    <div style={{ maxWidth: 980 }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Atlas analytics
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Model usage and cost over time — tasks done, API calls, tokens, and spend per model. Cost is
          cache-aware: input tokens served from the provider's prefix cache are billed at the cheaper rate.
        </p>
      </header>

      {/* Date range */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => {
              setFrom(isoDaysAgo(p.days - 1));
              setTo(isoDaysAgo(0));
            }}
            style={presetBtn}
          >
            {p.label}
          </button>
        ))}
        <span style={{ width: 1, height: 20, background: "var(--hairline)" }} />
        <label style={dateLbl}>
          From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={dateInput} />
        </label>
        <label style={dateLbl}>
          To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={dateInput} />
        </label>
      </div>

      {isLoading || !data ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : (
        <>
          {/* Totals */}
          <div style={totalsRow}>
            <Metric label="Tasks done" value={fmtInt(t!.tasks_done)} />
            <Metric label="API calls" value={fmtInt(t!.api_calls)} />
            <Metric label="Input tokens" value={fmtInt(t!.input_tokens)} />
            <Metric label="Output tokens" value={fmtInt(t!.output_tokens)} />
            <Metric label="Cached input" value={pct(t!.cached_input_tokens, t!.input_tokens)} />
            <Metric label="Cost" value={fmtUsd(t!.cost_usd)} accent />
          </div>

          {/* Per-model table */}
          <section style={card}>
            <h2 style={cardTitle}>By model</h2>
            {data.by_model.length === 0 ? (
              <p style={{ color: "var(--mute)", fontSize: 13, margin: 0 }}>No coding runs in this range.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--mute)", fontSize: 11.5 }}>
                    <th style={th}>Model</th>
                    <th style={thR}>Tasks</th>
                    <th style={thR}>API calls</th>
                    <th style={thR}>Input tok</th>
                    <th style={thR}>Output tok</th>
                    <th style={thR}>Cached</th>
                    <th style={thR}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_model.map((m) => (
                    <tr key={m.model} style={{ borderTop: "1px solid var(--hairline)" }}>
                      <td style={td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 2, background: colorFor(m.model) }} />
                          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{m.model}</span>
                        </span>
                      </td>
                      <td style={tdR}>{fmtInt(m.tasks_done)}</td>
                      <td style={tdR}>{fmtInt(m.api_calls)}</td>
                      <td style={tdR}>{fmtInt(m.input_tokens)}</td>
                      <td style={tdR}>{fmtInt(m.output_tokens)}</td>
                      <td style={tdR} title={`${fmtInt(m.cached_input_tokens)} of ${fmtInt(m.input_tokens)} input tokens served from cache`}>
                        {pct(m.cached_input_tokens, m.input_tokens)}
                      </td>
                      <td style={{ ...tdR, fontWeight: 600 }}>{fmtUsd(m.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Daily cost, stacked by model */}
          {byDay.length > 0 && (
            <section style={card}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
                <h2 style={{ ...cardTitle, margin: 0 }}>Daily cost by model</h2>
                <span style={{ fontSize: 12, color: "var(--mute)" }}>peak {fmtUsd(maxDayCost)}/day</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 14, overflowX: "auto", paddingBottom: 2 }}>
                {byDay.map(([d, rows]) => {
                  const total = rows.reduce((a, r) => a + r.cost_usd, 0);
                  return (
                    <div key={d} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, minWidth: 46 }}>
                      {/* cost value above the bar — read it directly */}
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap" }}>
                        {fmtUsd(total)}
                      </span>
                      <div
                        title={rows.map((r) => `${r.model}: ${fmtUsd(r.cost_usd)}`).join("\n")}
                        style={{
                          display: "flex", flexDirection: "column-reverse", height: 150, width: 34,
                          borderRadius: 4, overflow: "hidden", background: "var(--surface-2)",
                          border: "1px solid var(--hairline)",
                        }}
                      >
                        {rows.map((r) => (
                          <div
                            key={r.model}
                            style={{
                              // min 3% so a tiny non-zero day is still visible.
                              height: `${Math.max(3, (r.cost_usd / maxDayCost) * 100)}%`,
                              background: colorFor(r.model),
                            }}
                          />
                        ))}
                      </div>
                      <span style={{ fontSize: 11, color: "var(--mute)", whiteSpace: "nowrap" }}>
                        {fmtDate(d)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 18, marginTop: 16, flexWrap: "wrap" }}>
                {data.by_model.map((m) => (
                  <span key={m.model} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--body-mid)" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: colorFor(m.model) }} />
                    {m.model} · <strong style={{ color: "var(--ink)", fontWeight: 600 }}>{fmtUsd(m.cost_usd)}</strong>
                  </span>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={metric}>
      <span style={{ fontSize: 22, fontWeight: 600, color: accent ? "var(--ink)" : "var(--ink)" }}>{value}</span>
      <span style={{ fontSize: 11.5, color: "var(--mute)" }}>{label}</span>
    </div>
  );
}

const presetBtn: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 4,
  padding: "5px 10px", fontSize: 12, color: "var(--ink)", cursor: "pointer",
};
const dateLbl: CSSProperties = { fontSize: 12, color: "var(--mute)", display: "inline-flex", alignItems: "center", gap: 6 };
const dateInput: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 4,
  padding: "4px 8px", fontSize: 12.5, color: "var(--ink)",
};
const totalsRow: CSSProperties = {
  display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap",
};
const metric: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 2, padding: "12px 16px", flex: "1 1 130px",
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8,
};
const card: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8, padding: 16, marginBottom: 16,
};
const cardTitle: CSSProperties = { fontSize: 14, fontWeight: 600, margin: "0 0 12px" };
const th: CSSProperties = { padding: "0 8px 8px 0", fontWeight: 500 };
const thR: CSSProperties = { ...th, textAlign: "right" };
const td: CSSProperties = { padding: "8px 8px 8px 0" };
const tdR: CSSProperties = { ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 };
